import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { Container, Form, Nav, Tab } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { InfoCircle } from "react-bootstrap-icons";
import IndiceCriterios from "../../../../components/IndiceCriterios/IndiceCriterios";
import CadastroTabV2, { CadastroTabV2RefHandle } from "./tabs/CadastroTabV2";
import DocumentosTabV2, {
  DocumentosTabV2RefHandle,
} from "./tabs/DocumentosTabV2";
import "../V1/ShippingV1.css";
import {
  getSolicitacaoAnalise,
  getSolicitacaoDetalhe,
  patchAvaliacaoSolicitacao,
  type DadosSindicatoDetalhe,
  type SolicitacaoAnalise,
  type SolicitacaoDetalhe,
} from "../../../../services";
import { solicitacaoDetalheFromAnalise } from "../../Analysis/V2/solicitacaoDetalheFromAnalise";
import {
  buildAnaliseCamposPorSecaoId,
  buildCiclosHistoricoAdequacaoPorAnaliseKey,
  buildHistoricoAdequacaoPorAnaliseKey,
} from "../../Analysis/V2/buildAnaliseCamposPorSecaoId";
import type {
  PatchAvaliacaoSolicitacaoPayload,
  RequestRespostaCampoJson,
} from "../../../../services";
import {
  SecaoDocumento,
  analiseCriterioComRespostaAposSolicitacao,
  listarAnalisesCriterio,
  buildDestaqueAdequacaoSindicatoPorSecaoId,
  buildIndiceItems,
  mapAdequacaoAnalisePorSecaoId,
  mapSolicitacaoDetalheToSecoes,
} from "../../../../domain/shipping";
import ConfirmacaoEnvioV2Modal from "./components/ConfirmacaoEnvioV2Modal/ConfirmacaoEnvioV2Modal";
import ConsultaEnvioDocumentacaoV2 from "./components/ConsultaEnvioDocumentacaoV2/ConsultaEnvioDocumentacaoV2";
import {
  STORAGE_SHIPPING_V2_SOLICITACAO,
  STORAGE_SHIPPING_V2_YEAR,
} from "../../../../constants/shippingV2Session";
import { PATHS } from "../../../../routes/paths";
import { useAuth } from "../../../../context/AuthContext";
import { podeChamarGetAnaliseAvaliacao } from "../../../../components/Menu/SidebarMenu/menu.role";
import { scrollContentAreaToElement } from "utils/scrollContentArea";

function readPersistedSolicitacaoId(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_SHIPPING_V2_SOLICITACAO);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function readPersistedYear(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_SHIPPING_V2_YEAR);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function normalizarStatusGeralAnalise(nome: string | null | undefined): string {
  return String(nome ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .trim()
    .replace(/\s+/g, " ");
}

function pickFirstNonEmptyString(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v !== "string") continue;
    const t = v.trim();
    if (t) return t;
  }
  return null;
}

function resolveEnviadoPeloSindicatoEm(payload: unknown): string | null {
  const p = payload as Record<string, unknown> | null | undefined;
  return pickFirstNonEmptyString(
    p?.enviadoPeloSindicatoEm,
    p?.enviadoPeloSindicatoEM,
    p?.EnviadoPeloSindicatoEm,
    p?.EnviadoPeloSindicatoEM,
  );
}

function detalheComAnaliseToSolicitacaoAnalise(
  detalhe: SolicitacaoDetalhe | null | undefined,
): SolicitacaoAnalise | null {
  if (!detalhe?.grupos?.length) return null;
  const temAnalise = detalhe.grupos.some((g) =>
    (g.criterios || []).some((c) => (c as any).analise != null),
  );
  if (!temAnalise) return null;
  return {
    id: detalhe.id,
    dados: detalhe.dados,
    status: detalhe.status ?? null,
    grupos: detalhe.grupos as SolicitacaoAnalise["grupos"],
    receitasOperacionais: [],
    associados: [],
  };
}

function inferirDadosAdequacaoPorDetalheComAnalise(
  detalhe: SolicitacaoDetalhe | null | undefined,
): {
  adequacaoSolicitadaEm: string | null;
  adequacaoRealizadaEm: string | null;
  criteriosLiberadosParaAdequacaoIds: number[] | null;
} {
  if (!detalhe?.grupos?.length) {
    return {
      adequacaoSolicitadaEm: null,
      adequacaoRealizadaEm: null,
      criteriosLiberadosParaAdequacaoIds: null,
    };
  }
  let minSolicitacaoPendente: string | null = null;
  let maxAdequadoEm: string | null = null;
  const liberados = new Set<number>();
  for (const g of detalhe.grupos) {
    for (const c of g.criterios || []) {
      const arr = listarAnalisesCriterio((c as any).analise);
      for (const a of arr) {
        const temSolicitado =
          Boolean(a?.solicitadoEm) && String(a.solicitadoEm).trim() !== "";
        if (!temSolicitado) continue;
        const respondida = analiseCriterioComRespostaAposSolicitacao(a);
        if (!respondida) {
          liberados.add(c.id);
          const s = String(a.solicitadoEm);
          if (!minSolicitacaoPendente || new Date(s) < new Date(minSolicitacaoPendente)) {
            minSolicitacaoPendente = s;
          }
        } else if (a.adequadoEm) {
          const d = String(a.adequadoEm);
          if (!maxAdequadoEm || new Date(d) > new Date(maxAdequadoEm)) {
            maxAdequadoEm = d;
          }
        }
      }
    }
  }
  return {
    adequacaoSolicitadaEm: minSolicitacaoPendente,
    adequacaoRealizadaEm: minSolicitacaoPendente ? null : maxAdequadoEm,
    criteriosLiberadosParaAdequacaoIds:
      liberados.size > 0 ? Array.from(liberados) : null,
  };
}

const TABS = [
  { id: "resumo", label: "RESUMO" },
  { id: "cadastro", label: "CADASTRO" },
  { id: "doc-obrigatorios", label: "DOC. OBRIGATÓRIOS" },
  { id: "doc-bonus", label: "DOC. BÔNUS" },
  { id: "doc-bonus-extra", label: "DOC. BÔNUS EXTRA" },
] as const;

type IndiceLink = {
  tabKey?: string;
  id?: string;
  label?: string;
  isPending?: boolean;
  isDestaqueAlteracao?: boolean;
  isAdequacaoAguardando?: boolean;
  isAdequacaoRespondida?: boolean;
};

const ShippingV2: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { perfil, loading: authLoading } = useAuth();
  const podeChamarAnaliseNoEnvio = useMemo(
    () => podeChamarGetAnaliseAvaliacao(perfil),
    [perfil],
  );

  const state = (location.state || {}) as {
    selectedYear?: number;
    item?: any;
    mode?: string;
    idQuestionarioResposta?: number | null;
  };

  const idFromRouterState: number | null =
    typeof state?.item?.id === "number"
      ? state.item.id
      : typeof state?.item?.idQuestionarioResposta === "number"
        ? state.item.idQuestionarioResposta
        : null;

  const resolvedSolicitacaoId = useMemo(() => {
    if (typeof idFromRouterState === "number" && idFromRouterState > 0) {
      return idFromRouterState;
    }
    return readPersistedSolicitacaoId();
  }, [idFromRouterState]);

  const selectedYear =
    typeof state?.selectedYear === "number" && state.selectedYear > 0
      ? state.selectedYear
      : readPersistedYear() ?? 2026;

  useEffect(() => {
    if (typeof idFromRouterState === "number" && idFromRouterState > 0) {
      sessionStorage.setItem(STORAGE_SHIPPING_V2_SOLICITACAO, String(idFromRouterState));
      sessionStorage.setItem(STORAGE_SHIPPING_V2_YEAR, String(selectedYear));
    }
  }, [idFromRouterState, selectedYear]);

  const [activeTab, setActiveTab] = useState<string>("cadastro");
  const [loading, setLoading] = useState(false);
  const [isIndiceOpen, setIsIndiceOpen] = useState(false);

  const isChangingTabRef = useRef(false);

  const cadastroRef = useRef<CadastroTabV2RefHandle>(null);
  const obrigatoriosRef = useRef<DocumentosTabV2RefHandle>(null);
  const bonusRef = useRef<DocumentosTabV2RefHandle>(null);
  const bonusExtraRef = useRef<DocumentosTabV2RefHandle>(null);

  const [secoesObrigatorios, setSecoesObrigatorios] = useState<SecaoDocumento[]>([]);
  const [secoesBonus, setSecoesBonus] = useState<SecaoDocumento[]>([]);
  const [secoesBonusExtra, setSecoesBonusExtra] = useState<SecaoDocumento[]>([]);

  const [pendenciasObrigatorios, setPendenciasObrigatorios] = useState<Record<string, boolean>>({});
  const [pendenciasBonus, setPendenciasBonus] = useState<Record<string, boolean>>({});
  const [pendenciasBonusExtra, setPendenciasBonusExtra] = useState<Record<string, boolean>>({});

  const [confirmacaoEnvioOpen, setConfirmacaoEnvioOpen] = useState(false);

  const [sindicatoDados, setSindicatoDados] = useState<DadosSindicatoDetalhe | null>(null);
  // Já nasce "carregando" quando existe solicitação a buscar, evitando o flash inicial
  // de campos vazios antes do useEffect de carregamento setar este estado.
  const [loadingSolicitacaoDetalhe, setLoadingSolicitacaoDetalhe] = useState(
    () => resolvedSolicitacaoId != null,
  );
  const [enviadoEmDoDetalhe, setEnviadoEmDoDetalhe] = useState<string | null>(null);
  const [adequacaoSolicitadaEm, setAdequacaoSolicitadaEm] = useState<string | null>(null);
  const [adequacaoRealizadaEm, setAdequacaoRealizadaEm] = useState<string | null>(null);
  const [criteriosLiberadosParaAdequacaoIds, setCriteriosLiberadosParaAdequacaoIds] =
    useState<number[] | null>(null);
  
  const [analiseEnvioOpcional, setAnaliseEnvioOpcional] =
    useState<SolicitacaoAnalise | null>(null);

  const itemFromLista = state?.item as
    | {
      id?: number;
      envioDocumentacao?: string | null;
      situacao?: string;
      enviadoPeloSindicatoEm?: string | null;
      enviadoPeloSindicatoEM?: string | null;
      EnviadoPeloSindicatoEm?: string | null;
      EnviadoPeloSindicatoEM?: string | null;
      adequacaoSolicitadaEm?: string | null;
      adequacaoRealizadaEm?: string | null;
    }
    | undefined;

  const envioJaRegistradoNaLista = useMemo(() => {
    if (!itemFromLista) return false;
    if (itemFromLista.envioDocumentacao) return true;
    if (resolveEnviadoPeloSindicatoEm(itemFromLista)) return true;
    return (
      itemFromLista.situacao === "Documentação Enviada" ||
      itemFromLista.situacao === "Análise Pendente" ||
      itemFromLista.situacao === "Análise em Andamento"
    );
  }, [itemFromLista]);

  
  const adequacaoPendente = useMemo(() => {
    const solicitada =
      adequacaoSolicitadaEm ?? itemFromLista?.adequacaoSolicitadaEm ?? null;
    const realizada =
      adequacaoRealizadaEm ?? itemFromLista?.adequacaoRealizadaEm ?? null;
    return Boolean(solicitada) && !realizada;
  }, [
    adequacaoSolicitadaEm,
    adequacaoRealizadaEm,
    itemFromLista?.adequacaoSolicitadaEm,
    itemFromLista?.adequacaoRealizadaEm,
  ]);

  const temEnvioRegistrado = useMemo(
    () => envioJaRegistradoNaLista || Boolean(enviadoEmDoDetalhe),
    [envioJaRegistradoNaLista, enviadoEmDoDetalhe],
  );

  const documentacaoSomenteLeitura = useMemo(() => {
    return temEnvioRegistrado && !adequacaoPendente;
  }, [temEnvioRegistrado, adequacaoPendente]);

  const enviadoEmExibicao = useMemo(
    () =>
      enviadoEmDoDetalhe ??
      resolveEnviadoPeloSindicatoEm(itemFromLista) ??
      null,
    [enviadoEmDoDetalhe, itemFromLista],
  );

  useEffect(() => {
    setEnviadoEmDoDetalhe(null);
    setAdequacaoSolicitadaEm(null);
    setAdequacaoRealizadaEm(null);
    setCriteriosLiberadosParaAdequacaoIds(null);
    setAnaliseEnvioOpcional(null);
  }, [resolvedSolicitacaoId]);

  const historicoAdequacaoPorAnaliseKey = useMemo(
    () => buildHistoricoAdequacaoPorAnaliseKey(analiseEnvioOpcional),
    [analiseEnvioOpcional],
  );

  const ciclosHistoricoAdequacaoPorAnaliseKey = useMemo(
    () => buildCiclosHistoricoAdequacaoPorAnaliseKey(analiseEnvioOpcional),
    [analiseEnvioOpcional],
  );

  const analiseGeralConcluida = useMemo(
    () =>
      normalizarStatusGeralAnalise(analiseEnvioOpcional?.status?.nome) ===
      "analise concluida",
    [analiseEnvioOpcional],
  );

  const observacaoAnalistaNaoConformePorCriterioId = useMemo(() => {
    if (!analiseGeralConcluida) return {};
    const camposPorCriterioId = buildAnaliseCamposPorSecaoId(analiseEnvioOpcional);
    const out: Record<string, (string | React.ReactElement)[]> = {};
    for (const [criterioId, campos] of Object.entries(camposPorCriterioId)) {
      if (campos.status !== "NAO_CONFORME") continue;
      const observacao = campos.observacaoAnalista?.trim();
      if (!observacao) continue;
      out[criterioId] = [
        <div
          key="analise-nao-conforme"
          className="p-3 rounded border bg-warning-subtle border-warning"
        >
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted mb-0">Situação</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              disabled
              className="bg-light"
              value="NÃO CONFORME"
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label className="small text-muted mb-0">
              Observação analista
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              disabled
              className="bg-light"
              value={observacao}
            />
          </Form.Group>
        </div>,
      ];
    }
    return out;
  }, [analiseEnvioOpcional, analiseGeralConcluida]);

  const refetchDetalhe = useCallback(async () => {
    if (!resolvedSolicitacaoId) return;

    try {
      setLoadingSolicitacaoDetalhe(true);
      // Não limpar sindicatoDados aqui: some o cadastro vazio durante o refetch e,
      // se a API falhar ou retornar vazio, o usuário perde a tela do cadastro.

      const analisePrimeiro = podeChamarAnaliseNoEnvio
        ? await getSolicitacaoAnalise(resolvedSolicitacaoId)
        : null;
      const detalheDireto = analisePrimeiro
        ? null
        : await getSolicitacaoDetalhe(resolvedSolicitacaoId);
      const detalheBase =
        detalheDireto ??
        (analisePrimeiro ? solicitacaoDetalheFromAnalise(analisePrimeiro) : null);
      if (!detalheBase) return;
      const inferido = inferirDadosAdequacaoPorDetalheComAnalise(detalheBase);
      const detalhe = {
        ...detalheBase,
        adequacaoSolicitadaEm:
          detalheBase.adequacaoSolicitadaEm ?? inferido.adequacaoSolicitadaEm,
        adequacaoRealizadaEm:
          detalheBase.adequacaoRealizadaEm ?? inferido.adequacaoRealizadaEm,
        criteriosLiberadosParaAdequacaoIds:
          detalheBase.criteriosLiberadosParaAdequacaoIds ??
          inferido.criteriosLiberadosParaAdequacaoIds,
      };

      const analiseEfetiva =
        analisePrimeiro ?? detalheComAnaliseToSolicitacaoAnalise(detalhe);
      setAnaliseEnvioOpcional(analiseEfetiva);

      setSindicatoDados(detalhe.dados ?? null);
      setEnviadoEmDoDetalhe(resolveEnviadoPeloSindicatoEm(detalhe));
      setAdequacaoSolicitadaEm(detalhe.adequacaoSolicitadaEm ?? null);
      setAdequacaoRealizadaEm(detalhe.adequacaoRealizadaEm ?? null);
      setCriteriosLiberadosParaAdequacaoIds(
        detalhe.criteriosLiberadosParaAdequacaoIds ?? null,
      );

      const { obrigatorios, bonus, bonusExtra } =
        mapSolicitacaoDetalheToSecoes(detalhe);

      setSecoesObrigatorios(obrigatorios);
      setSecoesBonus(bonus);
      setSecoesBonusExtra(bonusExtra);
    } catch {
      // Sem log em produção: UI mantém dados atuais.
    } finally {
      setLoadingSolicitacaoDetalhe(false);
    }
  }, [resolvedSolicitacaoId, podeChamarAnaliseNoEnvio]);

  const handleSaveAll = useCallback(
    async () => {
    try {
      const hasUploadInProgress =
        Boolean(obrigatoriosRef.current?.hasPendingUploads?.()) ||
        Boolean(bonusRef.current?.hasPendingUploads?.()) ||
        Boolean(bonusExtraRef.current?.hasPendingUploads?.());

      if (hasUploadInProgress) {
        return {
          show: true,
          message: "Aguarde o término do upload dos arquivos antes de salvar.",
          type: "error",
        };
      }

      if (!resolvedSolicitacaoId) {
        return {
          show: true,
          message: "Não foi possível salvar: solicitação inválida.",
          type: "error",
        };
      }

      const payloadConsolidado: PatchAvaliacaoSolicitacaoPayload = {};
      const camposConsolidados: RequestRespostaCampoJson[] = [];
      const analiseCriterioSolicitacaoConsolidada: Array<{
        id: number;
        criterioId: number;
        observacaoSindicato?: string | null;
      }> = [];
      analiseCriterioSolicitacaoConsolidada.push(
        ...(obrigatoriosRef.current?.getAnaliseCriterioSolicitacaoAlterados?.() ?? []),
      );
      analiseCriterioSolicitacaoConsolidada.push(
        ...(bonusRef.current?.getAnaliseCriterioSolicitacaoAlterados?.() ?? []),
      );
      analiseCriterioSolicitacaoConsolidada.push(
        ...(bonusExtraRef.current?.getAnaliseCriterioSolicitacaoAlterados?.() ?? []),
      );

      const cadastroDirty = Boolean(cadastroRef.current?.isDirty?.());
      if (cadastroDirty) {
        const cadastroPatch = cadastroRef.current?.buildPatchPayload?.();
        if (cadastroPatch?.avaliacao) payloadConsolidado.avaliacao = cadastroPatch.avaliacao;
        if (cadastroPatch?.sindicato) payloadConsolidado.sindicato = cadastroPatch.sindicato;
      }

      const obrigatoriosDirty = Boolean(obrigatoriosRef.current?.isDirty?.());
      if (obrigatoriosDirty) {
        camposConsolidados.push(...(obrigatoriosRef.current?.getCamposAlterados?.() ?? []));
      }

      const bonusDirty = Boolean(bonusRef.current?.isDirty?.());
      if (bonusDirty) {
        camposConsolidados.push(...(bonusRef.current?.getCamposAlterados?.() ?? []));
      }

      const bonusExtraDirty = Boolean(bonusExtraRef.current?.isDirty?.());
      if (bonusExtraDirty) {
        camposConsolidados.push(...(bonusExtraRef.current?.getCamposAlterados?.() ?? []));
      }

      if (camposConsolidados.length > 0) {
        payloadConsolidado.campos = camposConsolidados;
      }
      if (analiseCriterioSolicitacaoConsolidada.length > 0) {
        payloadConsolidado.analiseCriterioSolicitacao =
          analiseCriterioSolicitacaoConsolidada;
      }

      const hasAnyChange =
        Boolean(payloadConsolidado.avaliacao) ||
        Boolean(payloadConsolidado.sindicato) ||
        (payloadConsolidado.campos?.length ?? 0) > 0 ||
        (payloadConsolidado.analiseCriterioSolicitacao?.length ?? 0) > 0;

      if (!hasAnyChange) {
        return {
          show: true,
          message: "Nenhuma alteração pendente para salvar.",
          type: "success",
        };
      }

      await patchAvaliacaoSolicitacao(resolvedSolicitacaoId, payloadConsolidado);

      await refetchDetalhe();

      // Garante que os filhos re-sincronizem com o GET antes de fixar o baseline (refs),
      // evitando baseline com id novo enquanto o estado local ainda reflete o payload antigo.
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });

      if (cadastroDirty) cadastroRef.current?.markAsSaved?.();
      if (obrigatoriosDirty) obrigatoriosRef.current?.markAsSaved?.();
      if (bonusDirty) bonusRef.current?.markAsSaved?.();
      if (bonusExtraDirty) bonusExtraRef.current?.markAsSaved?.();

      return {
        show: true,
        message: "Tudo salvo com sucesso!",
        type: "success",
      };
    } catch {
      return {
        show: true,
        message: "Erro ao salvar informações.",
        type: "error",
      };
    }
  },
  [refetchDetalhe, resolvedSolicitacaoId],
  );

  useEffect(() => {
    const carregarDados = async () => {
      if (!resolvedSolicitacaoId) {
        setLoadingSolicitacaoDetalhe(false);
        return;
      }
      if (authLoading) {
        setLoadingSolicitacaoDetalhe(true);
        return;
      }

      try {
        setLoadingSolicitacaoDetalhe(true);
        setSindicatoDados(null);

        const analisePrimeiro = podeChamarAnaliseNoEnvio
          ? await getSolicitacaoAnalise(resolvedSolicitacaoId)
          : null;
        const detalheDireto = analisePrimeiro
          ? null
          : await getSolicitacaoDetalhe(resolvedSolicitacaoId);
        const detalheBase =
          detalheDireto ??
          (analisePrimeiro
            ? solicitacaoDetalheFromAnalise(analisePrimeiro)
            : null);
        if (!detalheBase) return;
        const inferido = inferirDadosAdequacaoPorDetalheComAnalise(detalheBase);
        const detalhe = {
          ...detalheBase,
          adequacaoSolicitadaEm:
            detalheBase.adequacaoSolicitadaEm ?? inferido.adequacaoSolicitadaEm,
          adequacaoRealizadaEm:
            detalheBase.adequacaoRealizadaEm ?? inferido.adequacaoRealizadaEm,
          criteriosLiberadosParaAdequacaoIds:
            detalheBase.criteriosLiberadosParaAdequacaoIds ??
            inferido.criteriosLiberadosParaAdequacaoIds,
        };

        const analiseEfetiva =
          analisePrimeiro ?? detalheComAnaliseToSolicitacaoAnalise(detalhe);
        setAnaliseEnvioOpcional(analiseEfetiva);

        setSindicatoDados(detalhe.dados ?? null);
        setEnviadoEmDoDetalhe(resolveEnviadoPeloSindicatoEm(detalhe));
        setAdequacaoSolicitadaEm(detalhe.adequacaoSolicitadaEm ?? null);
        setAdequacaoRealizadaEm(detalhe.adequacaoRealizadaEm ?? null);
        setCriteriosLiberadosParaAdequacaoIds(
          detalhe.criteriosLiberadosParaAdequacaoIds ?? null,
        );

        const { obrigatorios, bonus, bonusExtra } =
          mapSolicitacaoDetalheToSecoes(detalhe);

        setSecoesObrigatorios(obrigatorios);
        setSecoesBonus(bonus);
        setSecoesBonusExtra(bonusExtra);
      } catch {
        // Sem log em produção: UI mostra estado de carregamento finalizado.
      } finally {
        setLoadingSolicitacaoDetalhe(false);
      }
    };

    carregarDados();
  }, [resolvedSolicitacaoId, authLoading, podeChamarAnaliseNoEnvio]);

  useEffect(() => {
    if (!secoesObrigatorios.length) return;

    setPendenciasObrigatorios((prev) => {
      const next = { ...prev };

      secoesObrigatorios.forEach((secao) => {
        if (next[secao.id] === undefined) {
          next[secao.id] = true;
        }
      });

      return next;
    });
  }, [secoesObrigatorios]);

  useEffect(() => {
    if (!secoesBonus.length) return;

    setPendenciasBonus((prev) => {
      const next = { ...prev };

      secoesBonus.forEach((secao) => {
        if (next[secao.id] === undefined) {
          next[secao.id] = true;
        }
      });

      return next;
    });
  }, [secoesBonus]);

  useEffect(() => {
    if (!secoesBonusExtra.length) return;

    setPendenciasBonusExtra((prev) => {
      const next = { ...prev };

      secoesBonusExtra.forEach((secao) => {
        if (next[secao.id] === undefined) {
          next[secao.id] = true;
        }
      });

      return next;
    });
  }, [secoesBonusExtra]);

  const todasSecoesPreenchidas = useMemo(() => {
    const completo = (pend: Record<string, boolean>, secoes: SecaoDocumento[]) => {
      if (secoes.length === 0) return true;
      return secoes.every((s) => pend[s.id] === false);
    };

    return (
      completo(pendenciasObrigatorios, secoesObrigatorios) &&
      completo(pendenciasBonus, secoesBonus) &&
      completo(pendenciasBonusExtra, secoesBonusExtra)
    );
  }, [
    pendenciasObrigatorios,
    pendenciasBonus,
    pendenciasBonusExtra,
    secoesObrigatorios,
    secoesBonus,
    secoesBonusExtra,
  ]);

  const handleEnviarPreConfirmacao = useCallback(async () => {
    if (!todasSecoesPreenchidas) {
      return {
        show: true,
        type: "error",
        message:
          "Preencha todos os critérios obrigatórios em todas as abas antes de enviar a documentação.",
      };
    }

    // Sem skipRefetchAfterPatch: o modal de confirmação exibe secoesObrigatorios/Bonus/BonusExtra
    // (estado alimentado pelo GET do refetch), não as respostas locais das abas. Pular o refetch
    // aqui fazia o modal abrir com os campos vazios quando o usuário clicava direto em "Enviar".
    const resultado = await handleSaveAll();
    const feedback = resultado as
      | { show?: boolean; message?: string; type?: string }
      | undefined
      | null;
    if (feedback?.type === "error") {
      return feedback;
    }

    setConfirmacaoEnvioOpen(true);
    return {
      show: true,
      type: "success",
      message: "Dados salvos. Revise o resumo na confirmação e finalize o envio.",
    };
  }, [handleSaveAll, todasSecoesPreenchidas]);

  const destaqueAlteracaoPorSecaoId = useMemo(() => {
    if (!adequacaoPendente) return {};
    const todasSecoes = [
      ...secoesObrigatorios,
      ...secoesBonus,
      ...secoesBonusExtra,
    ];
    return buildDestaqueAdequacaoSindicatoPorSecaoId(
      todasSecoes,
      criteriosLiberadosParaAdequacaoIds,
    );
  }, [
    adequacaoPendente,
    secoesObrigatorios,
    secoesBonus,
    secoesBonusExtra,
    criteriosLiberadosParaAdequacaoIds,
  ]);

  const todasSecoesParaAdequacao = useMemo(
    () => [...secoesObrigatorios, ...secoesBonus, ...secoesBonusExtra],
    [secoesObrigatorios, secoesBonus, secoesBonusExtra],
  );

  const {
    solicitadaAoSindicatoPorSecaoId,
    aguardandoRetornoSindicatoPorSecaoId,
    respondidaPeloSindicatoPorSecaoId,
  } = useMemo(
    () =>
      mapAdequacaoAnalisePorSecaoId(analiseEnvioOpcional, todasSecoesParaAdequacao),
    [analiseEnvioOpcional, todasSecoesParaAdequacao],
  );

  const indiceItems = useMemo(
    () =>
      buildIndiceItems(
        secoesObrigatorios,
        secoesBonus,
        secoesBonusExtra,
        pendenciasObrigatorios,
        pendenciasBonus,
        pendenciasBonusExtra,
        destaqueAlteracaoPorSecaoId,
        solicitadaAoSindicatoPorSecaoId,
        aguardandoRetornoSindicatoPorSecaoId,
        respondidaPeloSindicatoPorSecaoId,
      ),
    [
      secoesObrigatorios,
      secoesBonus,
      secoesBonusExtra,
      pendenciasObrigatorios,
      pendenciasBonus,
      pendenciasBonusExtra,
      destaqueAlteracaoPorSecaoId,
      solicitadaAoSindicatoPorSecaoId,
      aguardandoRetornoSindicatoPorSecaoId,
      respondidaPeloSindicatoPorSecaoId,
    ]
  );

  const aplicarNomesLocaisNasSecoes = useCallback(
    (secoes: SecaoDocumento[], nomesLocais?: Record<string, string>) => {
      if (!nomesLocais || Object.keys(nomesLocais).length === 0) return secoes;
      return secoes.map((secao) => {
        if (secao.id === "adimplencia" && secao.adimplenciaMeta?.initial) {
          const associados = secao.adimplenciaMeta.initial.associados.map((assoc, idx) => {
            const nomeLocal = nomesLocais[`adimplencia-assoc-${idx}`];
            if (!nomeLocal) return assoc;
            return { ...assoc, arquivoNomeExibicao: nomeLocal };
          });
          return {
            ...secao,
            adimplenciaMeta: {
              ...secao.adimplenciaMeta,
              initial: { ...secao.adimplenciaMeta.initial, associados },
            },
          };
        }

        const nomeLocal = nomesLocais[secao.id];
        if (!nomeLocal) return secao;
        return { ...secao, initialArquivoNomeExibicao: nomeLocal };
      });
    },
    [],
  );

  const secoesObrigatoriosParaResumo = aplicarNomesLocaisNasSecoes(
    secoesObrigatorios,
    obrigatoriosRef.current?.getNomesArquivoLocais?.(),
  );

  const secoesBonusParaResumo = aplicarNomesLocaisNasSecoes(
    secoesBonus,
    bonusRef.current?.getNomesArquivoLocais?.(),
  );

  const secoesBonusExtraParaResumo = aplicarNomesLocaisNasSecoes(
    secoesBonusExtra,
    bonusExtraRef.current?.getNomesArquivoLocais?.(),
  );

  const indiceTemPendencias = useMemo(() => {
    const grupoTemPendencia = (
      pend: Record<string, boolean>,
      secoes: SecaoDocumento[]
    ) => {
      if (secoes.length === 0) return false;
      return secoes.some((s) => pend[s.id] !== false);
    };
    return (
      grupoTemPendencia(pendenciasObrigatorios, secoesObrigatorios) ||
      grupoTemPendencia(pendenciasBonus, secoesBonus) ||
      grupoTemPendencia(pendenciasBonusExtra, secoesBonusExtra)
    );
  }, [
    pendenciasObrigatorios,
    pendenciasBonus,
    pendenciasBonusExtra,
    secoesObrigatorios,
    secoesBonus,
    secoesBonusExtra,
  ]);

  const indiceTemDestaqueAlteracao = useMemo(() => {
    for (const sec of indiceItems) {
      for (const link of sec.links || []) {
        if (link.isDestaqueAlteracao) return true;
      }
    }
    return false;
  }, [indiceItems]);

  const indiceTemAdequacaoAguardandoOuRespondida = useMemo(() => {
    for (const sec of indiceItems) {
      for (const link of sec.links || []) {
        if (link.isAdequacaoAguardando || link.isAdequacaoRespondida) return true;
      }
    }
    return false;
  }, [indiceItems]);

  const indiceBotaoComAlerta =
    indiceTemPendencias ||
    indiceTemDestaqueAlteracao ||
    indiceTemAdequacaoAguardandoOuRespondida;

  const visibleTabIds = useMemo(() => {
    const ids = new Set<string>(["cadastro"]);
    if (temEnvioRegistrado) ids.add("resumo");
    if (loadingSolicitacaoDetalhe || secoesObrigatorios.length > 0) ids.add("doc-obrigatorios");
    if (loadingSolicitacaoDetalhe || secoesBonus.length > 0) ids.add("doc-bonus");
    if (loadingSolicitacaoDetalhe || secoesBonusExtra.length > 0) ids.add("doc-bonus-extra");
    return ids;
  }, [
    temEnvioRegistrado,
    loadingSolicitacaoDetalhe,
    secoesObrigatorios.length,
    secoesBonus.length,
    secoesBonusExtra.length,
  ]);

  // O botão "ENVIAR" deve ficar na última aba de documentos com critérios visíveis,
  // pois nem toda solicitação tem critérios em bônus extra (ou até em bônus).
  const DOC_TAB_IDS = ["doc-obrigatorios", "doc-bonus", "doc-bonus-extra"] as const;
  const ultimaAbaDocComCriterios = useMemo(() => {
    for (let i = DOC_TAB_IDS.length - 1; i >= 0; i--) {
      if (visibleTabIds.has(DOC_TAB_IDS[i])) return DOC_TAB_IDS[i];
    }
    return null;
  }, [visibleTabIds]);

  // Solicitação com apenas critérios "somente análise" não gera nenhuma aba de
  // documentos (elas ficam de fora de mapSolicitacaoDetalheToSecoes). Nesse caso
  // o Cadastro já é a última etapa do sindicato: mostra SALVAR/ENVIAR em vez de
  // PRÓXIMO, senão o botão fica sem próxima aba para ir.
  const possuiSomenteAnalises =
    resolvedSolicitacaoId != null &&
    !loadingSolicitacaoDetalhe &&
    ultimaAbaDocComCriterios === null;

  useEffect(() => {
    if (!visibleTabIds.has(activeTab)) {
      setActiveTab(temEnvioRegistrado ? "resumo" : "cadastro");
    }
  }, [visibleTabIds, activeTab, temEnvioRegistrado]);

  const initialTabAppliedRef = useRef(false);

  useEffect(() => {
    initialTabAppliedRef.current = false;
  }, [resolvedSolicitacaoId]);

  useEffect(() => {
    if (initialTabAppliedRef.current) return;

    if (envioJaRegistradoNaLista) {
      setActiveTab("resumo");
      initialTabAppliedRef.current = true;
      return;
    }

    if (loadingSolicitacaoDetalhe) return;

    if (temEnvioRegistrado) {
      setActiveTab("resumo");
    }
    initialTabAppliedRef.current = true;
  }, [envioJaRegistradoNaLista, loadingSolicitacaoDetalhe, temEnvioRegistrado]);

  const getNextVisibleTab = useCallback(
    (currentTabId: string): string | null => {
      const currentIndex = TABS.findIndex((t) => t.id === currentTabId);
      for (let i = currentIndex + 1; i < TABS.length; i++) {
        if (visibleTabIds.has(TABS[i].id)) return TABS[i].id;
      }
      return null;
    },
    [visibleTabIds],
  );

  const handleTabChange = useCallback(
    async (nextTab: string | null) => {
      if (!nextTab) return;
      if (nextTab === activeTab) return;
      if (isChangingTabRef.current) return;

      isChangingTabRef.current = true;
      setLoading(true);

      try {
        setActiveTab(nextTab);
      } finally {
        setLoading(false);
        isChangingTabRef.current = false;
      }
    },
    [activeTab]
  );

  const handleIndiceNavigate = useCallback(
    (link: { path?: string; tabKey?: string; id?: string }) => {
      const l = link as IndiceLink;
      if (!l?.id) return;

      setIsIndiceOpen(false);

      const scrollToId = () => {
        const delay = l.tabKey && l.tabKey !== activeTab ? 350 : 150;

        setTimeout(() => {
          const el = document.getElementById(l.id!);
          scrollContentAreaToElement(el, { behavior: "smooth" });
        }, delay);
      };

      if (l.tabKey && l.tabKey !== activeTab) {
        handleTabChange(l.tabKey).then(() => scrollToId());
      } else {
        scrollToId();
      }
    },
    [activeTab, handleTabChange]
  );

  const descricaoObrigatorios =
    "Devem ser inseridas atas das assembleias de prestação de contas e o demonstrativo de resultado dos sindicatos, " +
    "assinados pelo Presidente e Contador e aprovados pelo Conselho Fiscal. " +
    "A documentação deve ser protocolada anualmente, até 30 de junho. " +
    '<a href="https://www.fiemg.com.br/estatuto" target="_blank" rel="noopener noreferrer">Clique aqui para ver o Estatuto</a>.';

  const carregandoPrimeiraCarga = loadingSolicitacaoDetalhe && !sindicatoDados;
  if (loading || carregandoPrimeiraCarga) return <PageLoading minHeight="80vh" />;

  const exibindoResumo = activeTab === "resumo";
  const renderReadOnlyAlert = (nomeAba: string) =>
    documentacaoSomenteLeitura ? (
      <div className="shipping-v2-readonly-alert" role="alert">
        Você não pode mais editar os dados de {nomeAba}.
      </div>
    ) : null;

  return (
    <Container className="shipping-container shipping-doc-v2-font" style={{ position: "relative" }}>
      {!exibindoResumo && (
        <div
          className="merit-voltar-seta"
          title="Voltar para a lista"
          onClick={() =>
            navigate(PATHS.DOC_SHIPPING, { state: { refreshSolicitacoes: true } })
          }
          style={{
            position: "absolute",
            top: "16px",
            right: "15px",
            zIndex: 20,
          }}
        >
          <FaArrowLeft />
        </div>
      )}

      <IndiceCriterios
        open={isIndiceOpen}
        onClose={() => setIsIndiceOpen(false)}
        items={indiceItems}
        onNavigate={handleIndiceNavigate}
      />

      {!exibindoResumo && (
        <button
          type="button"
          onClick={() => setIsIndiceOpen(true)}
          className="shipping-index-btn shipping-index-btn--floating"
          aria-label={
            indiceBotaoComAlerta
              ? "Abrir índice de critérios. Existem itens pendentes de preenchimento ou critérios liberados para adequação."
              : "Abrir índice de critérios"
          }
          title={
            indiceBotaoComAlerta
              ? "Índice de critérios — pendências ou critérios a adequar"
              : "Índice de critérios"
          }
        >
          {indiceBotaoComAlerta ? (
            <span className="shipping-index-btn-badge" aria-hidden />
          ) : null}
          <InfoCircle className="shipping-index-btn-icon" aria-hidden size={22} />
        </button>
      )}

      <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
        <div className="shipping-tabs-row" data-merit-ic-below>
          <Nav variant="tabs" className="shipping-tabs">
            {TABS.filter((tab) => visibleTabIds.has(tab.id)).map((tab) => (
              <Nav.Item key={tab.id}>
                <Nav.Link eventKey={tab.id} className="shipping-tab">
                  {tab.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>

        <Tab.Content className="shipping-content">
          <Tab.Pane eventKey="resumo">
            {resolvedSolicitacaoId != null ? (
              <ConsultaEnvioDocumentacaoV2
                solicitacaoId={resolvedSolicitacaoId}
                dadosSindicato={sindicatoDados}
                secoesObrigatorios={secoesObrigatoriosParaResumo}
                secoesBonus={secoesBonusParaResumo}
                secoesBonusExtra={secoesBonusExtraParaResumo}
                enviadoEm={enviadoEmExibicao}
                onVoltarLista={() =>
                  navigate(PATHS.DOC_SHIPPING, {
                    state: { refreshSolicitacoes: true },
                  })
                }
              />
            ) : null}
          </Tab.Pane>

          <Tab.Pane eventKey="cadastro">
            {possuiSomenteAnalises && (
              <div className="shipping-v2-somente-analise-alert" role="alert">
                <InfoCircle
                  className="shipping-v2-somente-analise-alert-icon"
                  aria-hidden
                />
                <div className="shipping-v2-somente-analise-alert-text">
                  <div>
                    Esta solicitação possui somente critérios de análise interna
                    pela FIEMG.
                  </div>
                  <div>
                    Não é necessário enviar documentação adicional. Confira os
                    dados cadastrais abaixo e clique em enviar para encaminhar a
                    solicitação para análise.
                  </div>
                </div>
              </div>
            )}
            {renderReadOnlyAlert("Cadastro")}
            <CadastroTabV2
              ref={cadastroRef}
              isActive={activeTab === "cadastro"}
              selectedYear={selectedYear}
              isLoadingDados={loadingSolicitacaoDetalhe}
              sindicatoDados={sindicatoDados ?? undefined}
              solicitacaoId={resolvedSolicitacaoId}
              onSaveAll={handleSaveAll}
              onProximo={() => handleTabChange(getNextVisibleTab("cadastro"))}
              {...(possuiSomenteAnalises
                ? {
                    submitLabel: "ENVIAR",
                    submitDisabled: !todasSecoesPreenchidas,
                    onEnviarPreConfirmacao: handleEnviarPreConfirmacao,
                  }
                : {})}
              readOnly={documentacaoSomenteLeitura}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="doc-obrigatorios">
            {renderReadOnlyAlert("Documentos Obrigatórios")}
            <DocumentosTabV2
              ref={obrigatoriosRef}
              isActive={activeTab === "doc-obrigatorios"}
              tipo="obrigatorios"
              tituloAba="DOCUMENTOS OBRIGATÓRIOS"
              descricaoGeral={descricaoObrigatorios}
              secoes={secoesObrigatorios}
              solicitacaoId={resolvedSolicitacaoId}
              onProximo={() => handleTabChange(getNextVisibleTab("doc-obrigatorios"))}
              onStatusChange={setPendenciasObrigatorios}
              onSaveAll={handleSaveAll}
              {...(ultimaAbaDocComCriterios === "doc-obrigatorios"
                ? {
                    submitLabel: "ENVIAR",
                    submitDisabled: !todasSecoesPreenchidas,
                    onEnviarPreConfirmacao: handleEnviarPreConfirmacao,
                  }
                : {})}
              historicoAdequacaoPorAnaliseKey={historicoAdequacaoPorAnaliseKey}
              ciclosHistoricoAdequacaoPorAnaliseKey={
                ciclosHistoricoAdequacaoPorAnaliseKey
              }
              parecerPorSecaoId={observacaoAnalistaNaoConformePorCriterioId}
              readOnly={documentacaoSomenteLeitura}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="doc-bonus">
            {renderReadOnlyAlert("Documentos Bônus")}
            <DocumentosTabV2
              ref={bonusRef}
              isActive={activeTab === "doc-bonus"}
              tipo="bonus"
              tituloAba="DOCUMENTOS BÔNUS"
              secoes={secoesBonus}
              solicitacaoId={resolvedSolicitacaoId}
              onProximo={() => handleTabChange(getNextVisibleTab("doc-bonus"))}
              onStatusChange={setPendenciasBonus}
              onSaveAll={handleSaveAll}
              {...(ultimaAbaDocComCriterios === "doc-bonus"
                ? {
                    submitLabel: "ENVIAR",
                    submitDisabled: !todasSecoesPreenchidas,
                    onEnviarPreConfirmacao: handleEnviarPreConfirmacao,
                  }
                : {})}
              historicoAdequacaoPorAnaliseKey={historicoAdequacaoPorAnaliseKey}
              ciclosHistoricoAdequacaoPorAnaliseKey={
                ciclosHistoricoAdequacaoPorAnaliseKey
              }
              parecerPorSecaoId={observacaoAnalistaNaoConformePorCriterioId}
              readOnly={documentacaoSomenteLeitura}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="doc-bonus-extra">
            {renderReadOnlyAlert("Documentos Bônus Extra")}
            <DocumentosTabV2
              ref={bonusExtraRef}
              isActive={activeTab === "doc-bonus-extra"}
              tipo="bonus-extra"
              tituloAba="DOC. BÔNUS EXTRA"
              secoes={secoesBonusExtra}
              solicitacaoId={resolvedSolicitacaoId}
              onStatusChange={setPendenciasBonusExtra}
              onSaveAll={handleSaveAll}
              {...(ultimaAbaDocComCriterios === "doc-bonus-extra"
                ? {
                    submitLabel: "ENVIAR",
                    submitDisabled: !todasSecoesPreenchidas,
                    onEnviarPreConfirmacao: handleEnviarPreConfirmacao,
                  }
                : {})}
              historicoAdequacaoPorAnaliseKey={historicoAdequacaoPorAnaliseKey}
              ciclosHistoricoAdequacaoPorAnaliseKey={
                ciclosHistoricoAdequacaoPorAnaliseKey
              }
              parecerPorSecaoId={observacaoAnalistaNaoConformePorCriterioId}
              readOnly={documentacaoSomenteLeitura}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <ConfirmacaoEnvioV2Modal
        show={confirmacaoEnvioOpen}
        onHide={() => setConfirmacaoEnvioOpen(false)}
        solicitacaoId={resolvedSolicitacaoId ?? null}
        dadosSindicato={sindicatoDados}
        secoesObrigatorios={secoesObrigatoriosParaResumo}
        secoesBonus={secoesBonusParaResumo}
        secoesBonusExtra={secoesBonusExtraParaResumo}
        modoEnvio={adequacaoPendente ? "adequacao" : "documentacao"}
        onEnvioSuccess={() =>
          navigate(PATHS.DOC_SHIPPING, {
            state: { refreshSolicitacoes: true },
          })
        }
      />
    </Container>
  );
};

export default ShippingV2;
